/**
 * lightThemeFlag — the ONE cross-app read/write path for `lwa_light_theme_v1`,
 * the parent-chosen "Sunny Light" design-language flag (the bright alternate
 * look for the child app shell; OFF = the classic Cosmic Dark design).
 *
 * WHY here in ui-shared: the parent portal (web-app) toggles this flag but
 * CANNOT import web-child (`@child`) at runtime, where the signed-envelope
 * writer (`flagGuard.setRigorFlag`) lives. So the reader AND a parent-gated
 * writer live here, over the shared `createSignedFlag` primitive (signedFlag.ts)
 * that replicates flagGuard.ts's scheme byte-for-byte — so an envelope written
 * here is accepted by flagGuard's `readGuardedFlag` (and vice-versa), and the
 * ops hook `window.__lwaRigorFlags.set('lwa_light_theme_v1', …)` stays
 * interoperable. `lwa_light_theme_v1` is registered in web-child/flagGuard.ts
 * GUARDED_FLAGS + DEFAULT_OFF_FLAGS (default OFF) too.
 *
 * DEFAULT OFF: an absent key reads false — shipping this changes nothing until
 * a parent signs it ON. A bare / legacy / foreign localStorage write ('on',
 * '1', 'true', …) is IGNORED; only a correctly PARENT-signed `{ v:'on' }`
 * envelope flips it on, so a child (or a stray script) can never switch the
 * theme themselves. Purely cosmetic, but it uses the parent-settings pattern
 * so every parent-owned toggle behaves identically. Never throws.
 */

import { createSignedFlag } from './signedFlag';

export const LIGHT_THEME_FLAG = 'lwa_light_theme_v1';

/** Window event dispatched after a successful parent write, so an already-
 *  mounted shell can re-render into the new design without a reload. */
export const LIGHT_THEME_EVENT = 'lwa:light-theme';

/** Announce a theme change to any mounted shell (best-effort; never throws). */
function announceThemeChange(enabled: boolean): void {
  try {
    window.dispatchEvent(new CustomEvent(LIGHT_THEME_EVENT, { detail: { enabled } }));
  } catch {
    /* SSR/tests without a window — the next mount reads the flag anyway */
  }
}

const flag = createSignedFlag(LIGHT_THEME_FLAG, { onAfterWrite: announceThemeChange }); // DEFAULT OFF

/**
 * True when a PARENT is signed in on this device: a structurally valid,
 * unexpired `auth_token` JWT (the parent-portal session — the same identity
 * flagGuard.hasParentGate rides on). The child token (`child_auth_token`) does
 * NOT qualify.
 */
export function hasLightThemeParentGate(): boolean {
  return flag.hasParentGate();
}

/**
 * Read the Sunny Light theme flag. Default OFF (= classic dark design).
 * Returns true ONLY for a correctly parent-signed `{ v:'on', sig }` envelope;
 * every other shape (absent, bare 'on'/'1'/'true', malformed JSON,
 * wrong/missing signature) reads false. Never throws.
 */
export function isLightThemeEnabled(): boolean {
  return flag.isEnabled();
}

/**
 * Parent-gated write. Turning it OFF (back to the default dark design) clears
 * the key; turning it ON stores a signed `{ v:'on', sig }` envelope. Refuses
 * (returns false, writes nothing) without a live parent session — so a
 * child-side or stray write can never flip the theme. Announces the change
 * (LIGHT_THEME_EVENT) after either successful write.
 */
export function setLightThemeEnabled(enabled: boolean): boolean {
  return flag.setEnabled(enabled);
}

/** TEST-ONLY seam: sign the flag ON without the parent-JWT check, so unit tests
 *  can exercise the Sunny Light paths (mirrors flagGuard's __setGuardedFlagOnForTests). */
export function __setLightThemeOnForTests(): void {
  flag.__setOnForTests();
}
